import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star } from "lucide-react";
import { Reveal, MaskLine, FadeIn, EASE } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import TrustStrip from "../components/TrustStrip";
import ClosingBlock from "../components/ClosingBlock";
import { PRODUCTS, SETS, CLINIC } from "../data/catalog";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="overflow-hidden pt-32 md:pt-40" data-testid="home-hero">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <FadeIn delay={0.1}>
          <div className="flex items-center gap-4">
            <span className="h-px w-12 bg-ink/30" />
            <p className="eyebrow" data-testid="hero-eyebrow">Ayushi · by Dr. Shibina · Thiruvananthapuram</p>
          </div>
        </FadeIn>
        <h1 className="mt-8 max-w-4xl font-serif font-light leading-[1.04] text-[11vw] text-ink sm:text-6xl lg:text-7xl" data-testid="hero-headline">
          <MaskLine delay={0.2}>Ayurveda, from someone</MaskLine>
          <MaskLine delay={0.32}>
            who <span className="italic text-sage-deep">actually practises it.</span>
          </MaskLine>
        </h1>
        <FadeIn delay={0.65} className="mt-8 max-w-xl">
          <p className="text-base leading-relaxed text-ink/70 md:text-lg" data-testid="hero-body">
            Ten products, formulated by Dr. Shibina Shemim and made in our own licensed facility in
            Thiruvananthapuram. Not a brand that borrowed the word Ayurveda — a working Ayurvedic
            clinic that started making what its patients kept asking for.
          </p>
        </FadeIn>
        <FadeIn delay={0.8} className="mt-10 flex flex-wrap items-center gap-6">
          <Link to="/shop" data-testid="hero-shop-cta" className="group inline-flex items-center gap-3 rounded-full bg-ink px-8 py-4 text-[13px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-ink-deep">
            Shop the Range
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link to="/our-story" data-testid="hero-story-cta" className="group inline-flex items-center gap-2 border-b border-ink/30 pb-1 text-[13px] uppercase tracking-[0.2em] text-ink/70 transition-colors hover:border-ink hover:text-ink">
            Meet Dr. Shibina
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </FadeIn>
        <FadeIn delay={0.95} className="mt-10">
          <a href={CLINIC.googleUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 text-[12px] uppercase tracking-[0.22em] text-ink/50 transition-colors hover:text-ink" data-testid="hero-social-proof">
            <Star className="h-3.5 w-3.5 fill-gold text-gold" />
            {CLINIC.rating} on Google · {CLINIC.reviewCount} reviews · Delivered across India
          </a>
        </FadeIn>
      </div>
      <motion.div style={{ y }} className="mx-auto mt-14 max-w-7xl px-4 md:px-8">
        <motion.div
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1.4, delay: 0.7, ease: EASE }}
        >
          <img
            src="/assets/range-hero.png"
            alt="The full Ayushi range with Dr. Shibina"
            className="w-full object-cover"
            data-testid="hero-range-image"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

const CarryingLine = () => (
  <section className="mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28" data-testid="carrying-line">
    <div className="grid gap-10 lg:grid-cols-12">
      <Reveal className="lg:col-span-7">
        <p className="eyebrow">Why Ayushi exists</p>
        <h2 className="mt-6 font-serif text-4xl font-light leading-[1.12] md:text-[54px]">
          Most Ayurvedic products have{" "}
          <span className="italic text-sage-deep">never met an Ayurvedic doctor.</span>
        </h2>
      </Reveal>
      <div className="lg:col-span-4 lg:col-start-9">
        <Reveal delay={0.15}>
          <p className="text-base leading-relaxed text-ink/70">
            The shelves are crowded with Ayurvedic labels made by contract manufacturers and
            designed by marketing teams. Ayushi is different in one plain way: Dr. Shibina Shemim
            is a practising Ayurvedic physician with a BAMS and a clinic. She writes the formulas,
            they are made under her own licence, and her name is on every label.
          </p>
          <Link to="/our-story" data-testid="carrying-line-cta" className="group mt-8 inline-flex items-center gap-2 border-b border-ink/30 pb-1 text-[12px] uppercase tracking-[0.22em] text-ink/70 transition-colors hover:border-ink hover:text-ink">
            Read Her Story
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const CATEGORY_CARDS = [
  { label: "Hair Care", count: "6 products", img: "/assets/p-kaachenna-hair-oil.jpg", cat: "Hair Care" },
  { label: "Skin Care", count: "3 products", img: "/assets/p-nalpamaradi-face-cream.jpg", cat: "Skin Care" },
  { label: "Body Care", count: "1 product", img: "/assets/p-foot-balm.jpg", cat: "Body Care" },
  { label: "Sets & Gifting", count: "4 sets", img: "/assets/p-lip-balm.jpg", cat: "Sets & Gifting" },
];

const RangeSection = () => (
  <section className="border-t hairline bg-white/50" data-testid="range-section">
    <div className="mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28">
      <Reveal>
        <p className="eyebrow">The range</p>
        <h2 className="mt-4 max-w-2xl font-serif text-4xl font-light leading-tight md:text-5xl">
          Ten products. <span className="italic text-sage-deep">That's the whole range.</span>
        </h2>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-ink/70">
          Hair, skin and body. Nothing we would not use ourselves. The range is not built to fill
          shelves — every product in it began with a patient asking.
        </p>
      </Reveal>
      <div className="mt-12 grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
        {CATEGORY_CARDS.map((c, i) => (
          <Reveal key={c.label} delay={i * 0.08}>
            <Link to={`/shop?cat=${encodeURIComponent(c.cat)}`} data-testid={`category-card-${c.cat.toLowerCase().replace(/\s|&/g, "-")}`} className="group block">
              <div className="overflow-hidden">
                <img src={c.img} alt={c.label} loading="lazy" className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <h3 className="font-serif text-xl italic">{c.label}</h3>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-ink/45">{c.count}</p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-ink/40 transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-ink" />
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <Link to="/shop" data-testid="range-see-all" className="group mt-10 inline-flex items-center gap-3 rounded-full bg-ink px-8 py-4 text-[12px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-ink-deep">
          See All Ten
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const Bestsellers = () => (
  <section className="mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28" data-testid="bestsellers-section">
    <Reveal>
      <p className="eyebrow">Bestsellers</p>
      <h2 className="mt-4 font-serif text-4xl font-light leading-tight md:text-5xl">
        Where most <span className="italic text-sage-deep">people start.</span>
      </h2>
    </Reveal>
    <div className="mt-12 grid grid-cols-2 gap-x-4 gap-y-10 md:gap-x-6 lg:grid-cols-4">
      {PRODUCTS.filter((p) => p.bestseller).map((p, i) => (
        <ProductCard key={p.id} product={p} index={i} />
      ))}
    </div>
  </section>
);

const MadeHere = () => (
  <section className="border-t hairline bg-white/50" data-testid="made-here-section">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 md:px-8 md:py-28 lg:grid-cols-12">
      <Reveal className="lg:col-span-6">
        <p className="eyebrow">How it's made</p>
        <h2 className="mt-4 font-serif text-4xl font-light leading-tight md:text-5xl">
          Made here. By us. <span className="italic text-sage-deep">In small batches.</span>
        </h2>
        <p className="mt-6 max-w-lg text-base leading-relaxed text-ink/70">
          Every product is made under our own cosmetic manufacturing licence, in our own facility
          in Thiruvananthapuram. Not outsourced, not white-labelled, not bought in and relabelled.
          Batches stay small enough for Dr. Shibina to check each one.
        </p>
        <Link to="/how-we-make-it" data-testid="made-here-cta" className="group mt-8 inline-flex items-center gap-2 border-b border-ink/30 pb-1 text-[12px] uppercase tracking-[0.22em] text-ink/70 transition-colors hover:border-ink hover:text-ink">
          See How We Make It
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8">
        <div className="overflow-hidden">
          <img src="/assets/img-a4.jpeg" alt="Small-batch preparation at the Ayushi facility" loading="lazy" className="aspect-[4/3] w-full object-cover" />
        </div>
      </Reveal>
    </div>
  </section>
);

const ClinicBehind = () => (
  <section className="mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28" data-testid="clinic-section">
    <div className="grid items-center gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <div className="overflow-hidden rounded-t-[999px]">
          <img src="/assets/img-b3.jpeg" alt="Dr. Shibina Shemim" className="aspect-[4/5] w-full object-cover" data-testid="clinic-doctor-photo" />
        </div>
      </Reveal>
      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal>
          <p className="eyebrow">The clinic behind it</p>
          <h2 className="mt-4 font-serif text-4xl font-light leading-tight md:text-5xl">
            There's a clinic <span className="italic text-sage-deep">behind this.</span>
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-ink/70">
            Ayushi Ayurveda Wellness has been treating patients at Chavadimukku, Sreekaryam for
            years. Consultations with Dr. Shibina run Monday to Saturday. The products came from
            the clinic — made for patients first, and for everyone else after.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/the-clinic" data-testid="clinic-visit-cta" className="group inline-flex items-center gap-3 rounded-full bg-ink px-7 py-3.5 text-[12px] uppercase tracking-[0.18em] text-cream transition-colors hover:bg-ink-deep">
              Visit the Clinic
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a href={CLINIC.whatsapp} target="_blank" rel="noreferrer" data-testid="clinic-book-cta" className="inline-flex items-center gap-3 rounded-full border border-ink/30 px-7 py-3.5 text-[12px] uppercase tracking-[0.18em] text-ink/75 transition-colors hover:border-ink hover:text-ink">
              Book a Consultation
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Reviews = () => (
  <section className="border-t hairline bg-white/50" data-testid="reviews-section">
    <div className="mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28">
      <div className="mx-auto max-w-2xl text-center">
        <Reveal>
          <div className="flex justify-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-5 w-5 fill-gold text-gold" />
            ))}
          </div>
          <h2 className="mt-6 font-serif text-4xl font-light leading-tight md:text-5xl">
            {CLINIC.rating} on Google. <span className="italic text-sage-deep">{CLINIC.reviewCount} reviews.</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink/70">
            Written by patients, over years, without being asked.
          </p>
          <a href={CLINIC.googleUrl} target="_blank" rel="noreferrer" data-testid="reviews-google-link" className="group mt-8 inline-flex items-center gap-2 border-b border-ink/30 pb-1 text-[12px] uppercase tracking-[0.22em] text-ink/70 transition-colors hover:border-ink hover:text-ink">
            Read them on Google
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

const Home = () => (
  <div data-testid="home-page">
    <Hero />
    <TrustStrip />
    <CarryingLine />
    <RangeSection />
    <Bestsellers />
    <Marquee items={["Made by a doctor", "Small batches", "Our own facility", "SLS & paraben free", "Ten products only", "Thiruvananthapuram"]} />
    <MadeHere />
    <ClinicBehind />
    <Reviews />
    <ClosingBlock />
  </div>
);

export default Home;
